import { useMemo } from 'react'
import { useI18n } from '../../i18n'
import type { AssetInventoryExecutiveAsset } from '../../services/enterpriseOperationalUxApi'
import styles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

type Props = {
  assets: AssetInventoryExecutiveAsset[]
}

const NODE_W = 100
const NODE_H = 36
const NODE_GAP_X = 10
const GROUP_PAD = 12
const GROUP_GAP_X = 20
const GROUP_GAP_Y = 48
const GROUPS_PER_ROW = 3
const LABEL_H = 16
const MAX_ASSETS = 36
const MAX_PER_GROUP = 8

type GraphNode = {
  id: string
  label: string
  x: number
  y: number
  variant: string
  groupKey: string
  groupLabel: string
}

function systemKey(sys: { informationSystemId?: string; id?: string; code?: string; name?: string }): string {
  return String(sys.informationSystemId || sys.id || sys.code || sys.name || '')
}

function interactionScore(a: AssetInventoryExecutiveAsset, b: AssetInventoryExecutiveAsset): number {
  let score = 0
  const aControls = new Set((a.blastRadius?.hits || []).map((h) => h.controlCode).filter(Boolean))
  for (const hit of b.blastRadius?.hits || []) {
    if (hit.controlCode && aControls.has(hit.controlCode)) score += 3
  }
  if (a.exposure.internetFacing && b.exposure.internetFacing) score += 1
  if ((a.pressure.degradedControls || 0) > 0 && (b.pressure.degradedControls || 0) > 0) score += 2
  if (a.vulnerability.criticalCount > 0 && b.vulnerability.criticalCount > 0) score += 1
  return score
}

export default function AssetInventoryAttackSurfaceMiniGraph({ assets }: Props) {
  const { t } = useI18n()

  const { nodes, edges, width, height, groups } = useMemo(() => {
    const ranked = [...assets]
      .sort((a, b) => {
        const score = (x: AssetInventoryExecutiveAsset) => {
          let s = 0
          if (x.exposure.exposureLevel === 'CRITICAL') s += 8
          else if (x.exposure.exposureLevel === 'HIGH') s += 5
          if (x.exposure.internetFacing) s += 4
          if ((x.pressure.degradedControls || 0) > 0) s += 3
          if (x.vulnerability.criticalCount > 0) s += 2
          return s
        }
        return score(b) - score(a)
      })
      .slice(0, MAX_ASSETS)

    const byGroup = new Map<string, { label: string; items: AssetInventoryExecutiveAsset[] }>()
    for (const asset of ranked) {
      const systems = asset.linkedInformationSystems || []
      if (!systems.length) {
        const key = '__unassigned__'
        const cur = byGroup.get(key) || {
          label: t('enterpriseOperationalUx.assetInventory.unassignedAssets', 'Unassigned to IS'),
          items: [],
        }
        cur.items.push(asset)
        byGroup.set(key, cur)
        continue
      }
      for (const sys of systems) {
        const key = systemKey(sys)
        if (!key) continue
        const cur = byGroup.get(key) || {
          label: String(sys.name || key),
          items: [],
        }
        if (!cur.items.some((a) => String(a.assetId) === String(asset.assetId))) {
          cur.items.push(asset)
        }
        byGroup.set(key, cur)
      }
    }

    const groupEntries = [...byGroup.entries()]
    const nodeList: GraphNode[] = []
    const groupMeta: Array<{ key: string; label: string; x: number; y: number; w: number; h: number }> = []
    const assetById = new Map<string, AssetInventoryExecutiveAsset>()

    let gridX = GROUP_PAD
    let gridY = GROUP_PAD + LABEL_H
    let rowMaxH = 0
    let maxRight = GROUP_PAD
    let maxBottom = gridY

    groupEntries.forEach(([groupKey, group], index) => {
      const items = group.items.slice(0, MAX_PER_GROUP)
      const cols = Math.min(4, Math.max(1, items.length))
      const rows = Math.ceil(items.length / cols) || 1
      const boxW = cols * (NODE_W + NODE_GAP_X) - NODE_GAP_X + GROUP_PAD * 2
      const boxH = rows * (NODE_H + NODE_GAP_X) - NODE_GAP_X + GROUP_PAD + LABEL_H

      if (index > 0 && index % GROUPS_PER_ROW === 0) {
        gridX = GROUP_PAD
        gridY += rowMaxH + GROUP_GAP_Y
        rowMaxH = 0
      }

      const boxX = gridX
      const boxY = gridY
      groupMeta.push({
        key: groupKey,
        label: group.label,
        x: boxX + GROUP_PAD,
        y: boxY,
        w: boxW,
        h: boxH,
      })

      let nx = boxX + GROUP_PAD
      let ny = boxY + LABEL_H
      items.forEach((a, i) => {
        const col = i % cols
        const row = Math.floor(i / cols)
        const id = String(a.assetId)
        assetById.set(id, a)
        const variant =
          a.exposure.exposureLevel === 'CRITICAL'
            ? styles.aiGraphNodeCritical
            : a.exposure.internetFacing
              ? styles.aiGraphNodeInternet
              : (a.pressure.degradedControls || 0) > 0
                ? styles.aiGraphNodeDegraded
                : a.vulnerability.criticalCount > 0
                  ? styles.aiGraphNodeVuln
                  : ''
        nodeList.push({
          id,
          label: a.name,
          x: nx + col * (NODE_W + NODE_GAP_X),
          y: ny + row * (NODE_H + NODE_GAP_X),
          variant,
          groupKey,
          groupLabel: group.label,
        })
      })

      rowMaxH = Math.max(rowMaxH, boxH)
      gridX += boxW + GROUP_GAP_X
      maxRight = Math.max(maxRight, gridX)
      maxBottom = Math.max(maxBottom, boxY + boxH)
    })

    const edgeList: Array<{ id: string; from: string; to: string; kind: 'chain' | 'interaction' }> = []
    const byGroupNodes = new Map<string, GraphNode[]>()
    for (const n of nodeList) {
      const list = byGroupNodes.get(n.groupKey) || []
      list.push(n)
      byGroupNodes.set(n.groupKey, list)
    }

    for (const list of byGroupNodes.values()) {
      for (let i = 0; i < list.length - 1; i++) {
        const a = assetById.get(list[i].id)
        const b = assetById.get(list[i + 1].id)
        const kind =
          a && b && interactionScore(a, b) > 0 ? ('interaction' as const) : ('chain' as const)
        edgeList.push({
          id: `chain-${list[i].id}-${list[i + 1].id}`,
          from: list[i].id,
          to: list[i + 1].id,
          kind,
        })
      }
    }

    const w = Math.max(320, maxRight + GROUP_PAD)
    const h = Math.max(140, maxBottom + GROUP_PAD)
    return { nodes: nodeList, edges: edgeList, width: w, height: h, groups: groupMeta }
  }, [assets, t])

  if (!assets.length) {
    return (
      <p className={styles.aiWidgetEmpty}>
        {t('assets.inventory.empty', 'No assets in this organization yet.')}
      </p>
    )
  }

  if (!nodes.length) {
    return (
      <p className={styles.aiWidgetEmpty}>
        {t('enterpriseOperationalUx.assetInventory.noCriticalExposure', 'No critical exposure detected')}
      </p>
    )
  }

  return (
    <div className={styles.aiGraphWrap}>
      <p className={styles.aiGraphLegend}>
        {t(
          'enterpriseOperationalUx.assetInventory.graphLegendIs',
          'Grouped by information system. Solid edges: co-located assets; dashed: shared blast-radius / degradation links. Unlinked assets shown separately.',
        )}
      </p>
      <svg
        className={styles.aiGraphSvg}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={t('enterpriseOperationalUx.assetInventory.attackSurfaceGraph', 'Attack surface graph')}
      >
        {edges.map((e) => {
          const s = nodes.find((n) => n.id === e.from)
          const tg = nodes.find((n) => n.id === e.to)
          if (!s || !tg) return null
          return (
            <line
              key={e.id}
              x1={s.x + NODE_W / 2}
              y1={s.y + NODE_H}
              x2={tg.x + NODE_W / 2}
              y2={tg.y}
              className={`${styles.aiGraphEdge} ${
                e.kind === 'interaction' ? styles.aiGraphEdgeInteraction : styles.aiGraphEdgePropagates
              }`.trim()}
            />
          )
        })}
        {groups.map((g) => (
          <text key={g.key} x={g.x} y={g.y - 4} className={styles.aiGraphGroupLabel}>
            {g.label.length > 22 ? `${g.label.slice(0, 20)}…` : g.label}
          </text>
        ))}
        {nodes.map((n) => (
          <g key={n.id} transform={`translate(${n.x},${n.y})`}>
            <rect
              width={NODE_W}
              height={NODE_H}
              rx={8}
              className={`${styles.aiGraphNode} ${n.variant}`.trim()}
            />
            <text x={NODE_W / 2} y={NODE_H / 2 + 5} textAnchor="middle" className={styles.aiGraphLabel}>
              {n.label.length > 12 ? `${n.label.slice(0, 10)}…` : n.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
